import React from 'react';
import {
    Container,
    Header,
    Title,
    PasswordContainer,
    PasswordBox,
    PasswordTitle,
    Input,
    Button,
    ConversationList,
    ConversationCard,
    ConversationHeader,
    BusinessName,
    LastMessageTime,
    LastMessage,
    MessagesContainer,
    MessagesHeader,
    MessagesList,
    MessageBubble,
    MessageText,
    MessageMeta,
    ReplyContainer,
    ReplyInput,
    ErrorText,
    LoadingText,
    EmptyText,
    RefreshButton
} from './styles';

interface ISupportMessage {
    id: string;
    conversationId: string;
    businessId: string;
    businessName: string;
    senderType: 'business' | 'admin';
    senderEmail: string;
    senderName: string;
    text: string;
    createdAt: number;
}

interface IConversation {
    conversationId: string;
    businessId: string;
    businessName: string;
    lastMessage: string;
    lastMessageAt: number;
    lastSenderType: 'business' | 'admin';
}

interface IPresenterProps {
    isAuthenticated: boolean;
    password: string;
    passwordError: string;
    onPasswordChange: (value: string) => void;
    onPasswordSubmit: () => void;
    conversations: IConversation[];
    selectedConversation: IConversation | null;
    messages: ISupportMessage[];
    replyText: string;
    onReplyChange: (value: string) => void;
    onSelectConversation: (conversation: IConversation) => void;
    onSendReply: () => void;
    onCloseConversation: () => void;
    onRefresh: () => void;
    onLogout: () => void;
    loadingConversations: boolean;
    loadingMessages: boolean;
    sendingReply: boolean;
    error: string;
    formatTime: (timestamp: number) => string;
}

const Presenter: React.FC<IPresenterProps> = ({
    isAuthenticated,
    password,
    passwordError,
    onPasswordChange,
    onPasswordSubmit,
    conversations,
    selectedConversation,
    messages,
    replyText,
    onReplyChange,
    onSelectConversation,
    onSendReply,
    onCloseConversation,
    onRefresh,
    onLogout,
    loadingConversations,
    loadingMessages,
    sendingReply,
    error,
    formatTime
}) => {
    // Password screen
    if (!isAuthenticated) {
        return (
            <PasswordContainer>
                <PasswordBox>
                    <PasswordTitle>Admin Support</PasswordTitle>
                    <Input
                        type="password"
                        placeholder="Clave de admin"
                        value={password}
                        onChange={(e) => onPasswordChange(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && onPasswordSubmit()}
                    />
                    <Button
                        onClick={onPasswordSubmit}
                        disabled={loadingConversations}
                        style={{ width: '100%' }}
                    >
                        {loadingConversations ? 'Verificando...' : 'Entrar'}
                    </Button>
                    {passwordError && <ErrorText>{passwordError}</ErrorText>}
                </PasswordBox>
            </PasswordContainer>
        );
    }

    // Main admin panel
    return (
        <Container>
            <Header>
                <Title>Soporte - Conversaciones</Title>
                <div style={{ display: 'flex', gap: '8px' }}>
                    <RefreshButton onClick={onRefresh} disabled={loadingConversations}>
                        {loadingConversations ? 'Cargando...' : 'Actualizar'}
                    </RefreshButton>
                    <Button variant="secondary" onClick={onLogout}>
                        Salir
                    </Button>
                </div>
            </Header>

            {error && <ErrorText style={{ marginBottom: '16px' }}>{error}</ErrorText>}

            {loadingConversations && conversations.length === 0 ? (
                <LoadingText>Cargando conversaciones...</LoadingText>
            ) : conversations.length === 0 ? (
                <EmptyText>No hay conversaciones de soporte</EmptyText>
            ) : (
                <ConversationList>
                    {conversations.map((conversation) => (
                        <ConversationCard
                            key={conversation.conversationId}
                            selected={selectedConversation?.conversationId === conversation.conversationId}
                            onClick={() => onSelectConversation(conversation)}
                        >
                            <ConversationHeader>
                                <BusinessName>{conversation.businessName}</BusinessName>
                                <LastMessageTime>
                                    {conversation.lastMessageAt ? formatTime(conversation.lastMessageAt) : ''}
                                </LastMessageTime>
                            </ConversationHeader>
                            <LastMessage>
                                {conversation.lastSenderType === 'admin' ? 'Tu: ' : ''}
                                {conversation.lastMessage || 'Sin mensajes'}
                            </LastMessage>
                        </ConversationCard>
                    ))}
                </ConversationList>
            )}

            {/* Messages panel */}
            {selectedConversation && (
                <MessagesContainer>
                    <MessagesHeader>
                        <BusinessName>{selectedConversation.businessName}</BusinessName>
                        <Button variant="secondary" onClick={onCloseConversation}>
                            Cerrar
                        </Button>
                    </MessagesHeader>

                    {loadingMessages ? (
                        <LoadingText>Cargando mensajes...</LoadingText>
                    ) : (
                        <MessagesList>
                            {messages.map((message) => (
                                <MessageBubble key={message.id} isAdmin={message.senderType === 'admin'}>
                                    <MessageText>{message.text}</MessageText>
                                    <MessageMeta isAdmin={message.senderType === 'admin'}>
                                        {message.senderName} - {formatTime(message.createdAt)}
                                    </MessageMeta>
                                </MessageBubble>
                            ))}
                        </MessagesList>
                    )}

                    <ReplyContainer>
                        <ReplyInput
                            placeholder="Escribe tu respuesta..."
                            value={replyText}
                            onChange={(e) => onReplyChange(e.target.value)}
                            disabled={sendingReply}
                        />
                        <Button
                            onClick={onSendReply}
                            disabled={!replyText.trim() || sendingReply}
                        >
                            {sendingReply ? 'Enviando...' : 'Enviar'}
                        </Button>
                    </ReplyContainer>
                </MessagesContainer>
            )}
        </Container>
    );
};

export default Presenter;
